import {
  AllBfMetricListItems,
  BfMetricTileDisplayName,
  BfMetricTileSize,
  IBfMetricListItem,
  UnitSystem,
} from '@trackman/baseball-shared-components';
import { Spin3dTiles } from 'constants/Spin3dTiles';
import { action, makeObservable, observable } from 'mobx';
import PermissionStore from 'stores/PermissionStore';
import GameStore from './GameStore';

const getUnitSystem = () => {
  return localStorage.getItem(storageKey) as UnitSystem;
};

const storageKey = 'choosenUnitSystem';
const metricsKey = 'metrics-v1';
const metricsVersionKey = 'ld-metrics-version';
const firstVisitDate = 'firstVisitDate';
const metricSizeKey = 'metricsize';

export class LiveDashboardStore {
  @observable isSettingsMenuVisible = false;

  @observable darkMode: boolean;

  @observable tileSize: BfMetricTileSize = BfMetricTileSize.Small;

  @observable metrics: IBfMetricListItem[] = AllBfMetricListItems;

  @observable unitSystem: UnitSystem = getUnitSystem() ? getUnitSystem() : UnitSystem.Imperial;

  @observable metricsVersion = '2.0';

  permissionStore: PermissionStore;

  gameStore: GameStore;

  constructor(permissionStore: PermissionStore, gameStore: GameStore) {
    this.permissionStore = permissionStore;
    this.darkMode = localStorage.getItem('theme') === 'dark';
    this.gameStore = gameStore;

    makeObservable(this);
  }

  private hidePitchLocationTiles = (metrics: IBfMetricListItem[]) => {
    return metrics.filter((metric) => {
      return (
        metric.name !== BfMetricTileDisplayName.plateSide &&
        metric.name !== BfMetricTileDisplayName.plateHeight &&
        metric.name !== BfMetricTileDisplayName.strikeZone &&
        metric.name !== BfMetricTileDisplayName.strikeZoneDecision &&
        metric.name !== BfMetricTileDisplayName.strikeZone3d
      );
    });
  };

  // Spin3D is allowed per game, so the tiles follow the current game's permission in memory only and are
  // never saved disabled to localStorage. Applied both after the permissions and after the metrics load,
  // because either request can finish last.
  private withSpin3dPermission = (metrics: IBfMetricListItem[]): IBfMetricListItem[] => {
    const isEnabled = this.permissionStore.permissions.isSpin3dAllowed;

    return metrics.map((metric) =>
      (Spin3dTiles as string[]).includes(metric.type) && metric.isEnabled !== isEnabled ? { ...metric, isEnabled } : metric
    );
  };

  setFirstVisitDate = () => {
    const existingDate = localStorage.getItem(firstVisitDate);
    if (!existingDate) {
      const now = new Date();
      localStorage.setItem(firstVisitDate, now.toISOString());
    }
  };

  hasAWeekPassedSinceFirstVisit = () => {
    const storedDate = localStorage.getItem('firstVisitDate');
    if (!storedDate) return false;

    const firstVisit = new Date(storedDate);
    const now = new Date();
    // @ts-ignore
    const diffInMs = now - firstVisit;
    const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;

    return diffInMs >= oneWeekInMs;
  };

  @action initializeMetricsFromLocalStorage = (hidePitchLocation: boolean) => {
    const clientVersion = localStorage.getItem(metricsVersionKey);

    this.setFirstVisitDate();

    if (clientVersion !== this.metricsVersion) {
      let newMetrics = AllBfMetricListItems as IBfMetricListItem[];

      if (this.hasAWeekPassedSinceFirstVisit()) {
        newMetrics.forEach((metric) => (metric.isNew = false));
      }

      if (hidePitchLocation) {
        newMetrics = this.hidePitchLocationTiles(newMetrics);
      }

      localStorage.setItem(metricsKey, JSON.stringify(newMetrics));
      this.metrics = this.withSpin3dPermission(newMetrics);
      this.setMetricsVersion();
      return;
    }

    const storedMetrics = localStorage.getItem(metricsKey);
    const storedMetricsSize = localStorage.getItem(metricSizeKey);
    if (storedMetrics) {
      const parsedStoredMetrics = JSON.parse(storedMetrics) as IBfMetricListItem[];
      const fullMetrics = [...parsedStoredMetrics, ...AllBfMetricListItems.filter((m) => !parsedStoredMetrics.some((p) => p.id === m.id))];

      // Temporary remove Strike Zone Decision tile
      const index = fullMetrics.findIndex((item: IBfMetricListItem) => item.type === 'strikeZoneDecision');
      if (index > -1) {
        fullMetrics.splice(index, 1);
        localStorage.setItem(metricsKey, JSON.stringify(fullMetrics));
      }

      let newMetrics = this.removeDuplicates(fullMetrics);

      if (this.hasAWeekPassedSinceFirstVisit()) {
        newMetrics.forEach((metric) => (metric.isNew = false));
      }

      if (hidePitchLocation) {
        newMetrics = this.hidePitchLocationTiles(newMetrics);
      }

      this.metrics = this.withSpin3dPermission(newMetrics);
    }
    if (storedMetricsSize) {
      this.tileSize = JSON.parse(storedMetricsSize);
    }
  };

  removeDuplicates = (metrics: any[]) => {
    const metricTypes = new Set();
    const uniqueMetrics = metrics.filter((metric) => {
      if (metricTypes.has(metric.type)) {
        return false;
      }

      metricTypes.add(metric.type);
      return true;
    });

    return uniqueMetrics;
  };

  @action setDarkMode = (value: boolean): void => {
    this.darkMode = !value;
    localStorage.setItem('theme', !value ? 'dark' : 'light');
  };

  @action setTileSize = (size: BfMetricTileSize) => {
    this.tileSize = size;
    localStorage.setItem(metricSizeKey, size);
  };

  @action setMetrics = (newMetrics: IBfMetricListItem[]) => {
    this.metrics = newMetrics;
    const spin3dTilesArray: IBfMetricListItem[] = [];

    if (!this.permissionStore.permissions.isSpin3dAllowed) {
      Spin3dTiles.forEach((tile) => {
        if (!newMetrics.find((item) => item.type === tile)) {
          const spin3dTile = AllBfMetricListItems.find((item) => item.type === tile);

          if (spin3dTile) {
            spin3dTilesArray.push(spin3dTile);
          }
        }
      });
    }

    localStorage.setItem(metricsKey, JSON.stringify(spin3dTilesArray.concat(newMetrics)));
  };

  @action setIsSettingsMenuVisible = (isVisible: boolean) => {
    this.isSettingsMenuVisible = isVisible;
  };

  @action setUnitSystem = async (choosenUnitSystem: UnitSystem) => {
    localStorage.setItem(storageKey, choosenUnitSystem);
    this.unitSystem = choosenUnitSystem;
  };

  @action setMetricsVersion = () => {
    localStorage.setItem(metricsVersionKey, this.metricsVersion);
  };

  @action applySpin3dPermission = () => {
    this.metrics = this.withSpin3dPermission(this.metrics);
  };
}
